import { useEffect, useState } from 'react';
import { buildGoogleCalendarUrl, buildICSDataUrl, type CalendarEvent } from '../lib/calendar';
import { scheduleReminder, initReminders } from '../lib/reminders';

type ActionLedgerItem = {
  id: string;
  label: string;
  priority?: 'low' | 'medium' | 'high';
  status?: 'todo' | 'in_progress' | 'done';
  timeframe?: string;
  dueDate?: string;
  metric?: string;
};

type SummaryData = {
  title: string;
  summary?: string;
  keyTakeaways?: string[];
  // Back-compat
  actionItems?: string[];
  // New structured ledger
  actionLedger?: ActionLedgerItem[];
  analytics?: any;
};

const Pill = ({ label, tone = 'slate' }: { label: string; tone?: 'slate' | 'green' | 'amber' | 'red' }) => {
  const toneMap: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneMap[tone]}`}>{label}</span>
  );
};

const MoMDisplay = ({ summary }: { summary: SummaryData | null }) => {
  if (!summary) return null;
  const keyTakeaways = Array.isArray(summary.keyTakeaways) ? summary.keyTakeaways : [];
  const hasLedger = Array.isArray(summary.actionLedger) && summary.actionLedger.length > 0;
  const fallbackItems = Array.isArray(summary.actionItems) ? summary.actionItems : [];

  const toneForPriority = (p?: string) => (p === 'high' ? 'red' : p === 'low' ? 'slate' : 'amber');
  const toneForStatus = (s?: string) => (s === 'done' ? 'green' : s === 'in_progress' ? 'amber' : 'slate');

  // Track which reminders are set in this view
  const [reminderSetMap, setReminderSetMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Initialize lightweight reminder loop once view mounts
    try { initReminders(); } catch {}
  }, []);

  const toCalendarEvent = (item: ActionLedgerItem): CalendarEvent | null => {
    if (!item.dueDate) return null;
    const start = new Date(item.dueDate);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return {
      title: item.label.replace(/^FOCUS AREA:\s*/i, ''),
      description: `${summary?.title || 'Coaching Focus'}\n${item.metric ? 'Metric: ' + item.metric : ''}`.trim(),
      start,
      end,
      location: undefined,
    };
  };

  const handleAddGoogle = (item: ActionLedgerItem) => {
    const evt = toCalendarEvent(item);
    if (!evt) return;
    const url = buildGoogleCalendarUrl(evt);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadICS = (item: ActionLedgerItem) => {
    const evt = toCalendarEvent(item);
    if (!evt) return;
    const dataUrl = buildICSDataUrl(evt);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'hipo-action.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleRemind = async (item: ActionLedgerItem) => {
    if (!item.dueDate) return;
    const at = new Date(item.dueDate);
    if (isNaN(at.getTime())) return;
    await scheduleReminder({
      title: item.label.replace(/^FOCUS AREA:\s*/i, ''),
      body: summary?.title ? `Session: ${summary.title}` : undefined,
      at,
    });
    setReminderSetMap((prev) => ({ ...prev, [item.id]: true }));
  };

  return (
    <div className="my-4 rounded-2xl border border-amber-200/70 bg-amber-50/70 p-5 shadow-sm">
      <h3 className="text-lg font-semibold tracking-tight text-amber-900">{summary.title}</h3>
      {summary.summary && (
        <p className="mt-2 text-sm leading-relaxed text-amber-900/90">{summary.summary}</p>
      )}

      {keyTakeaways.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-amber-800">Key Takeaways</h4>
          <ul className="mt-1 list-disc list-outside space-y-1 pl-5 text-sm text-amber-900/90">
            {keyTakeaways.map((item, idx) => (
              <li key={`kt-${idx}`}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5">
        <h4 className="text-sm font-semibold text-amber-800">Action Ledger</h4>
        {hasLedger ? (
          <ul className="mt-2 space-y-2">
            {summary.actionLedger!.map((item) => {
              const remSet = !!reminderSetMap[item.id];
              return (
                <li key={item.id} className="rounded-xl border border-amber-200/70 bg-white/70 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium text-amber-900">
                      {item.label}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Pill label={(item.priority || 'medium').toUpperCase()} tone={toneForPriority(item.priority) as any} />
                      <Pill label={(item.status || 'todo').replace('_',' ').toUpperCase()} tone={toneForStatus(item.status) as any} />
                      {item.timeframe && <Pill label={item.timeframe} />}
                      {item.dueDate && <Pill label={`DUE ${new Date(item.dueDate).toLocaleDateString()}`} tone="red" />}
                    </div>
                  </div>
                  {item.metric && (
                    <p className="mt-1 text-xs text-amber-900/80">Metric: {item.metric}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {item.dueDate && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAddGoogle(item)}
                          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                        >
                          Add to Google Calendar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadICS(item)}
                          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                        >
                          Download .ics
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemind(item)}
                          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                        >
                          {remSet ? 'Reminder Set' : 'Remind Me'}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : fallbackItems.length > 0 ? (
          <ul className="mt-2 list-disc list-outside space-y-1 pl-5 text-sm text-amber-900/90">
            {fallbackItems.map((item, idx) => (
              <li key={`ai-${idx}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-amber-900/80">No actions captured yet.</p>
        )}
      </div>
    </div>
  );
};

export default MoMDisplay;
